import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "@eudonet/opal";
import axios from "axios";
import { logger } from "@/infrastructure/services/LoggerService";
import { getContactById } from "../infrastructure/adapters/contactService";
/**
 * Fetches a single contact aggregate on mount and whenever `id` changes.
 *
 * Separates the two failure modes the page treats differently:
 * - **404** → `notFound` (expected empty-state, no toast).
 * - **network / 5xx** → `loadError` plus an error toast carrying a retry action.
 *
 * A monotonic sequence guard discards responses from superseded requests so a
 * slow earlier fetch can't clobber a newer one.
 */
export function useContactDetail(id) {
    const { t } = useI18n();
    const { showToast } = useToast();
    const contact = ref(null);
    const loading = ref(false);
    const notFound = ref(false);
    const loadError = ref(false);
    let loadSeq = 0;
    async function load(opts) {
        const seq = ++loadSeq;
        const silent = opts?.silent === true;
        if (!silent) {
            loading.value = true;
            notFound.value = false;
            loadError.value = false;
        }
        try {
            const result = await getContactById(id.value);
            if (seq !== loadSeq)
                return;
            contact.value = result;
        }
        catch (e) {
            if (seq !== loadSeq)
                return;
            if (silent) {
                // Background refresh failed — keep the current view intact, just surface the error.
                logger.error("Silent reload failed for contact", e);
                showToast({
                    title: t("common.error"),
                    message: t("contact.loadError"),
                    ariaClose: t("form.dismissToast"),
                    status: "error",
                });
            }
            else {
                contact.value = null;
                if (axios.isAxiosError(e) && e.response?.status === 404) {
                    notFound.value = true;
                    logger.debug("Contact not found", id.value);
                }
                else {
                    loadError.value = true;
                    logger.error("Failed to load contact detail", e);
                    showToast({
                        title: t("common.error"),
                        message: t("contact.loadError"),
                        ariaClose: t("form.dismissToast"),
                        status: "error",
                        primaryAction: { label: t("common.retry"), onClick: () => load() },
                    });
                }
            }
        }
        finally {
            if (!silent && seq === loadSeq)
                loading.value = false;
        }
    }
    watch(id, () => load(), { immediate: true });
    return { contact, loading, notFound, loadError, reload: load };
}
